<template>
    <div :class="{ 'qa-creative__has-selection': selectedDeliverable }" class="qa-creative__body">
        <portal to="creativeSizeFilter">
            <div v-if="hasDeliverables">
                <Dropdown
                    placement="bottom-start"
                    trigger="custom"
                    :visible="sizeFilterIsVisible"
                    data-testid="qa-drawer__select--sizes"
                    @onClickoutside="toggleDropdown"
                >
                    <hox-dropdown-button @click="toggleDropdown">
                        <slot>Ad Sizes</slot>
                    </hox-dropdown-button>
                    <template #list>
                        <QaDrawerTabSelectTemplates
                            :visible-master-template-ids="visibleMasterTemplateIds"
                            @select="setVisibleMasterTemplateIds"
                        />
                    </template>
                </Dropdown>
            </div>
        </portal>

        <div ref="body" class="qa-creative__body-content" @click.self="unsetSelectedDeliverable">
            <div v-if="hasErrorLoadingDeliverables" class="qa-creative__alert-wrapper">
                <hox-alert type="danger">
                    <template #title>Uh oh!</template>
                    <template #content>
                        <p>Sorry! We could not get your ads this time. Try again a moment.</p>
                    </template>
                    <template #actionItems>
                        <Button type="primary" @click="getDeliverablesForCreative">Retry getting deliverables</Button>
                    </template>
                </hox-alert>
            </div>

            <template v-else-if="hasDeliverables">
                <template v-if="hasSizesSelected">
                    <QaBanner
                        v-for="constructedDeliverable of deliverablesSortedBySize"
                        v-show="isVisible(constructedDeliverable)"
                        :key="constructedDeliverable.idHash"
                        class="qa-creative__banner-wrapper"
                        :class="{
                            'qa-creative__banner-wrapper--visible': isVisible(constructedDeliverable),
                            'qa-creative__banner-wrapper--single-visible': isTheOnlyVisible(constructedDeliverable)
                        }"
                        :max-height="bodyDimensionsPixels.height"
                        :max-width="bodyDimensionsPixels.width"
                        :scaling-factor="scalingFactor"
                        :deliverable="constructedDeliverable"
                        :external-annotations="externalAnnotations"
                        :internal-annotations="internalAnnotations"
                        :is-selected="checkIsSelected(constructedDeliverable)"
                        :selected-annotation="selectedAnnotation"
                        :show-resolved="showResolved"
                        :playback-namespace="playbackNamespace"
                        @click.self="unsetSelectedDeliverable"
                        @annotationAdded="$emit('annotationAdded', $event)"
                        @annotationSelected="$emit('annotationSelected', $event)"
                        @select="setSelectedDeliverable(constructedDeliverable)"
                        @statusHasBeenUpdated="$emit('statusHasBeenUpdated')"
                        @userQaStatusHasBeenUpdated="$emit('userQaStatusHasBeenUpdated', $event)"
                    />
                </template>

                <!--
                When there no sizes selected (and as a result no deliverables to show).
                -->
                <hox-empty-message v-else class="qa-creative__empty-message">
                    <template #title>No sizes selected</template>
                    <template #content>
                        <p>Select at least one size to QA your ads.</p>
                    </template>
                </hox-empty-message>
            </template>

            <!--
                When there are sizes selected, but no deliverables to show (due to role
                view restrictions).
            -->
            <hox-empty-message v-else-if="!isLoadingDeliverables" class="qa-creative__empty-message">
                <template #title>No ads to QA</template>
                <template #content>
                    <p>No ads for your selected sizes.</p>
                </template>
            </hox-empty-message>
        </div>
    </div>
</template>

<script lang="ts">
import { ref, defineComponent, type PropType } from "vue";
import { mapStores } from "pinia";

import HoxEmptyMessage from "@/components/common/EmptyMessage.vue";
import QaBanner from "@/components/Qa/QaBanner.vue";
import QaDrawerTabSelectTemplates from "@/components/Qa/DrawerTab/QaDrawerTabSelectTemplates.vue";

import getPaginatedDeliverables from "@/services/deliverables/getPaginatedDeliverables";
import URLManager from "@/services/URLManager";
import { useScalingBannerWrapper } from "@/composables/useScalingBannerWrapper";

import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";
import type { ConstructedDeliverable, QaAnnotation } from "@/types/types";
import { DeliverableLibraryAction } from "@/store/modules/deliverableLibrary";

const QueryParams = {
    SelectedDeliverableId: "qad-sd",
    VisibleMasterTemplateIds: "qad-vmt"
};

const sortByWidth = (a: ConstructedDeliverable, b: ConstructedDeliverable) => {
    const widthA = a.masterTemplate.width;
    const widthB = b.masterTemplate.width;

    if (widthA !== widthB) {
        return widthA < widthB ? -1 : 1;
    }

    const heightA = a.masterTemplate.height;
    const heightB = b.masterTemplate.height;

    if (heightA === heightB) {
        return 0;
    }

    return heightA < heightB ? 1 : -1;
};

export default defineComponent({
    components: {
        QaDrawerTabSelectTemplates,
        HoxEmptyMessage,
        QaBanner
    },

    props: {
        deliverable: {
            type: Object as PropType<ConstructedDeliverable>,
            required: true
        },
        externalAnnotations: {
            type: Array as PropType<QaAnnotation[]>,
            required: true
        },
        internalAnnotations: {
            type: Array as PropType<QaAnnotation[]>,
            required: true
        },
        selectedAnnotation: {
            type: [Object, null] as PropType<QaAnnotation | null>
        },
        showResolved: {
            type: Boolean,
            default: false
        },
        showFullCreative: {
            type: Boolean,
            default: false
        },
        showMasterTemplateIds: {
            type: Array as PropType<string[]>,
            default() {
                return [];
            }
        },
        playbackNamespace: {
            type: String,
            required: true
        }
    },

    emits: [
        "statusHasBeenUpdated",
        "userQaStatusHasBeenUpdated",
        "loadingStarted",
        "loadingFinished",
        "visibleMasterTemplateIds",
        "annotationAdded",
        "annotationSelected",
        "load",
        "select"
    ],

    setup() {
        const body = ref(null);
        const { bodyDimensionsPixels, scalingFactor } = useScalingBannerWrapper({
            bodyRef: body,
            bodyWidthOffset: -76
        });

        return {
            body,
            bodyDimensionsPixels,
            scalingFactor
        };
    },

    data() {
        return {
            deliverables: [] as ConstructedDeliverable[],
            deliverablesRequestCounter: 0,
            existingDeliverableStatusIdHashes: new Set(),
            hasErrorLoadingDeliverables: false,
            isLoadingDeliverables: false,
            sizeFilterIsVisible: false,
            selectedDeliverable: null as ConstructedDeliverable | null,
            visibleMasterTemplateIds: [] as string[],
            urlManager: null as URLManager | null
        };
    },

    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),
        campaignId() {
            return this.$route.params.campaignId as string;
        },

        masterTemplateIds() {
            return this.$store.state.campaign.normalized.masterTemplateIds;
        },

        deliverablesSortedBySize() {
            const sorted = [...this.deliverables].sort(sortByWidth);

            return sorted.reduce<ConstructedDeliverable[]>((acc, curr) => {
                if (curr.masterTemplate.width === 300 && curr.masterTemplate.height === 250) {
                    return [curr].concat(acc);
                }
                return acc.concat(curr);
            }, []);
        },
        hasDeliverables() {
            return this.deliverables.length !== 0;
        },

        hasSizesSelected() {
            return this.visibleMasterTemplateIds.length;
        }
    },
    watch: {
        deliverable: {
            handler(deliverable) {
                if (this.deliverable !== null) {
                    /*
                        At the moment we're clearing any templates that are selected
                        and only showing the template of the deliverable.

                        We may want to persist templates through different deliverables/drawer
                        open and closes, or alternatively always show all sizes for a deliverable.
                    */
                    // this.visibleMasterTemplateIds = [ ...this.masterTemplateIds ];
                    this.getDeliverablesForCreative();
                    /*
                        In a single banner mode limit the view to the deliverable and mark it as selected
                    */
                    if (!this.showFullCreative) {
                        if (!this.showMasterTemplateIds.length) {
                            this.visibleMasterTemplateIds = [deliverable.masterTemplate._id];
                            this.setSelectedDeliverable(deliverable);
                        } else {
                            this.visibleMasterTemplateIds = this.showMasterTemplateIds;
                        }
                    }
                }
            },
            immediate: false
        },

        isLoadingDeliverables(val) {
            this.$emit(val ? "loadingStarted" : "loadingFinished");
        },

        visibleMasterTemplateIds(val: string[], oldVal: string[]) {
            const newVisibleMasterTemplateIds = val.filter(v => !oldVal.includes(v));

            // if we get a new visible template id we need to pull data
            if (newVisibleMasterTemplateIds.length) {
                this.getDeliverablesForCreative();
            }

            if (this.selectedDeliverable && !val.includes(this.selectedDeliverable.masterTemplate._id)) {
                this.unsetSelectedDeliverable();
            }

            this.setVisibleMasterTemplateIdsQueryParams();
            this.$emit("visibleMasterTemplateIds", val);
        }
    },

    beforeUnmount() {
        this.cleanDeliverableStatusByIdHash();
        this.unsetAllQueryParams();
    },
    created() {
        this.urlManager = new URLManager(this.$router, {
            tracked: Object.values(QueryParams)
        });
        /*
            We store a set of existing deliverable ID hashes so that we can
            clear out data created by this component without removing data that
            is being used in elsewhere.
        */
        this.existingDeliverableStatusIdHashes = new Set(
            Object.keys(this.$store.state.deliverableLibrary.deliverableStatusByIdHash)
        );

        const queryParamVisibleMasterTemplateIds = this.$route.query[QueryParams.VisibleMasterTemplateIds] as string[];

        if (queryParamVisibleMasterTemplateIds) {
            const visibleMasterTemplateIds = Array.isArray(queryParamVisibleMasterTemplateIds)
                ? queryParamVisibleMasterTemplateIds
                : [queryParamVisibleMasterTemplateIds];
            /*
                It's possible that some visibleMasterTemplateIds query params may no
                longer exist for a campaign. Instead of causing a hard error we
                proactively filter the data.
            */
            this.setVisibleMasterTemplateIds(
                visibleMasterTemplateIds.filter(
                    masterTemplateId => this.$store.state.campaign.normalized.masterTemplates[masterTemplateId]
                )
            );
        } else {
            this.setVisibleMasterTemplateIds([...this.masterTemplateIds]);
        }

        // In a single banner mode limit the view to the deliverable and mark it as selected
        if (!this.showFullCreative) {
            if (!this.showMasterTemplateIds.length) {
                this.visibleMasterTemplateIds = [this.deliverable.masterTemplate._id];
                this.setSelectedDeliverable(this.deliverable);
            } else {
                this.visibleMasterTemplateIds = this.showMasterTemplateIds;
            }
        }
        this.setVisibleMasterTemplateIdsQueryParams();
        this.getDeliverablesForCreative();
    },
    methods: {
        checkIsSelected(constructedDeliverable: ConstructedDeliverable) {
            return this.selectedDeliverable?.idHash === constructedDeliverable.idHash;
        },

        cleanDeliverableStatusByIdHash() {
            /*
                To ensure that `deliverableStatusByIdHash` in the store does not
                infinitely grow we need to clear out data that is no longer being used.

                To make sure that we do not clear out data that is being used elsewhere
                we filter out any deliverables that existed in `deliverableStatusByIdHash`
                before this component was created.
            */
            const deliverablesToUnset = this.deliverables.filter(
                deliverable => !this.existingDeliverableStatusIdHashes.has(deliverable.idHash)
            );
            this.$store.dispatch(DeliverableLibraryAction.UnsetDeliverableStatusByIdHash, deliverablesToUnset);
        },

        setVisibleMasterTemplateIds(visibleMasterTemplateIds: string[]) {
            this.visibleMasterTemplateIds = visibleMasterTemplateIds;
            if (
                this.selectedDeliverable &&
                !this.visibleMasterTemplateIds.includes(this.selectedDeliverable.masterTemplate._id)
            ) {
                this.unsetSelectedDeliverable();
            }
        },

        async getDeliverablesForCreative() {
            this.hasErrorLoadingDeliverables = false;
            const isMostRecentRequest = (requestCount: number) => this.deliverablesRequestCounter === requestCount;
            this.deliverablesRequestCounter += 1;
            this.cleanDeliverableStatusByIdHash();
            if (this.deliverable !== null && this.masterTemplateIds.length > 0) {
                const requestCount = this.deliverablesRequestCounter;
                this.isLoadingDeliverables = true;
                try {
                    const groupValuesForQuery = this.deliverable.combination.map(groupValue => {
                        const groupName = Object.keys(groupValue)[0];
                        return {
                            name: groupName,
                            valueIds: [groupValue[groupName].groupValueId]
                        };
                    });

                    // if we don't have the visible template then skip the call
                    if (!this.visibleMasterTemplateIds.length) {
                        return;
                    }

                    const libraryFilters = {
                        ...this.creativeLibraryFiltersStore.getConstructedLibraryFilters,
                        filters: {
                            masterTemplateIds: this.visibleMasterTemplateIds,
                            editableGroups: groupValuesForQuery,
                            languages: [this.deliverable.language]
                        }
                    };

                    const { deliverables } = await getPaginatedDeliverables({
                        campaignId: this.campaignId,
                        libraryFilters,
                        page: 1,
                        deliverablesPerPage: this.masterTemplateIds.length,
                        store: this.$store
                    });

                    if (!isMostRecentRequest(requestCount)) {
                        return;
                    }
                    this.$store.dispatch(DeliverableLibraryAction.SetDeliverableStatusByIdHash, deliverables);
                    this.deliverables = deliverables;
                    this.$emit("load", deliverables);
                } catch {
                    if (isMostRecentRequest(requestCount)) {
                        this.hasErrorLoadingDeliverables = true;
                    }
                } finally {
                    if (isMostRecentRequest(requestCount)) {
                        this.isLoadingDeliverables = false;
                    }
                }
            } else {
                this.isLoadingDeliverables = false;
                this.deliverables = [];
            }
        },

        isTheOnlyVisible(computedDeliverable: ConstructedDeliverable) {
            return (
                this.visibleMasterTemplateIds.includes(computedDeliverable.masterTemplate._id) &&
                this.visibleMasterTemplateIds.length === 1
            );
        },

        isVisible(computedDeliverable: ConstructedDeliverable) {
            return this.visibleMasterTemplateIds.includes(computedDeliverable.masterTemplate._id);
        },

        setSelectedDeliverable(deliverable: ConstructedDeliverable) {
            if (
                (!this.selectedDeliverable || this.selectedDeliverable.idHash !== deliverable.idHash) &&
                this.visibleMasterTemplateIds.includes(deliverable.masterTemplate._id)
            ) {
                this.selectedDeliverable = deliverable;
                this.$emit("select", this.selectedDeliverable);
            }
        },

        setVisibleMasterTemplateIdsQueryParams() {
            this.urlManager?.setURLParams(
                [
                    {
                        key: QueryParams.VisibleMasterTemplateIds,
                        value: [this.visibleMasterTemplateIds]
                    }
                ],
                true
            );
        },

        toggleDropdown() {
            this.sizeFilterIsVisible = !this.sizeFilterIsVisible;
        },

        unsetAllQueryParams() {
            this.urlManager?.setURLParams(
                Object.values(QueryParams).map(key => ({
                    key,
                    value: []
                })),
                true
            );
        },

        unsetSelectedDeliverable() {
            this.selectedDeliverable = null;
            this.$emit("select", null);
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.qa-creative__body {
    background: $cmp-light-bg-color;
    display: flex;
    flex: 1;

    height: auto;
    position: relative;
    overflow: auto;
}

.qa-creative__body-content {
    display: flex;
    flex-wrap: wrap;
    height: 100%;
    justify-content: flex-start;
    overflow: auto;
    padding: $spacing-large $spacing-large $spacing;
    width: 100%;
}

.qa-creative__alert-wrapper {
    width: 100%;
}

.qa-creative__banner-wrapper {
    // display: inline-block;
    vertical-align: top;
    flex-grow: 0;
    flex-shrink: 0;

    &--visible {
        margin: 0 $spacing $spacing 0;

        &.qa-creative__banner-wrapper--single-visible {
            margin: 0 auto $spacing auto;
        }
    }
}

.qa-creative__empty-message {
    flex: 1;
    margin: auto;
}

.qa-creative__has-selection {
    .qa-banner {
        opacity: 0.75;

        &--selected {
            opacity: 1;
        }
    }
}
</style>
